import { Link, LinkProps, RegisteredRouter, RouteIds, useMatches } from '@tanstack/react-router';

import { NavTabProps, NavTabs } from '@creditornot/alchemy-react';
import * as icons from '@creditornot/alchemy-react/icons';
import { useFeatureEnabled } from 'common/feature-flag/hooks/useFeatureEnabled';
import { useFormatMessage } from 'common/hooks/useFormatMessage';
import { useSelectedCountryCodes } from 'common/hooks/useSelectedCountryCodes';
import { useUser } from 'common/hooks/useUser';

import {
  clawbackListRouteApi,
  grirListRouteApi,
  inventoryInvoicesRouteApi,
  nonInventoryInvoicesRouteApi,
  tasksListLayoutRouteApi,
} from '../routeApi';

type TabItem = {
  label: NavTabProps<unknown>['label'];
  icon: NavTabProps<unknown>['icon'];
  to: LinkProps<RegisteredRouter>['to'];
  id: RouteIds<RegisteredRouter['routeTree']>;
  disabled?: boolean;
};

type Props = { className?: string };

export default function FinanceTabs({ className }: Props) {
  const format = useFormatMessage();
  const selectedCountryCodes = useSelectedCountryCodes();
  const isNonInventoryInvoicesEnabled = useFeatureEnabled(
    'non-inventory-invoices',
    selectedCountryCodes,
  );
  const isClawbacksEnabled = useFeatureEnabled('clawback-requests', selectedCountryCodes);
  const { hasRole } = useUser();
  const isDeveloper = hasRole('developer_wm');
  const matches = useMatches();

  const tabs: TabItem[] = [
    {
      label: format('finance.tasks'),
      to: '/finance/tasks',
      id: tasksListLayoutRouteApi.id,
      disabled: !isDeveloper,
      icon: icons.tap,
    },
    {
      label: format('invoices.inventory_invoices'),
      to: '/finance/invoices',
      id: inventoryInvoicesRouteApi.id,
      icon: icons.receipt,
    },
    {
      label: format('invoices.non_inventory_invoices'),
      to: '/finance/non-inventory-invoices',
      id: nonInventoryInvoicesRouteApi.id,
      disabled: !isNonInventoryInvoicesEnabled,
      icon: icons.receipt,
    },
    {
      label: format('finance.clawback_requests'),
      to: '/finance/clawback',
      id: clawbackListRouteApi.id,
      icon: icons.receiptRefund,
      disabled: !isClawbacksEnabled,
    },
    {
      label: format('finance.grir'),
      to: '/finance/grir',
      id: grirListRouteApi.id,
      icon: icons.repeat,
    },
  ];

  // Search all matches (not just the last) so a layout route id stays active
  // when its children are rendered (e.g. tasks ppv/pqv/unmatched sub-routes).
  const activeTabId =
    tabs.find((tab) => matches.some((match) => match.routeId === tab.id))?.id ??
    matches[matches.length - 1].routeId;

  return (
    <NavTabs
      activeTabId={activeTabId}
      className={className}
      aria-label={format('finance.finance_flows')}
    >
      {tabs
        .filter((tab) => !tab.disabled)
        .map((tab) => (
          <NavTabs.Tab
            key={tab.id}
            as={Link}
            id={tab.id}
            label={tab.label}
            to={tab.to}
            icon={tab.icon}
          />
        ))}
    </NavTabs>
  );
}
